// Fetches the set of YYYY-MM-DD dates with availability across all fitters
// at the currently-selected location for the currently-selected fitting type,
// over the 90-day booking horizon. Used by the Calendar to grey out days
// with no slots.
//
// Re-fetches when location, fitting type, or duration change. Returns null
// while loading or when prerequisites aren't met — Calendar treats null as
// "no filter applied" so all dates within range remain pickable until the
// fetch resolves (prevents the calendar from looking empty during the brief
// fetch window).

import { useEffect, useState } from 'react';
import { getAvailableDatesInRange } from '../api/client';
import { resolveWireName } from '../data/fittingNames';
import type { Location, FittingType } from '../types';

const BOOKING_HORIZON_DAYS = 90;

function isoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export interface AvailableDatesState {
  /**
   * Resolved set of YYYY-MM-DD strings with availability. `null` means
   * either prerequisites are missing (no location/fittingType) or the
   * first fetch hasn't started — Calendar treats this as "no filter".
   */
  availableDates: Set<string> | null;
  /**
   * True while a fetch is in flight. Calendar shows a loading state and
   * makes all cells unclickable during this window so the user doesn't
   * pick a date that's about to be disabled.
   */
  loading: boolean;
}

export function useAvailableDates(
  location: Location | null,
  fittingType: FittingType | null,
  resourceId?: string | null,
  fittingNameMap?: Record<string, string> | null,
): AvailableDatesState {
  const [dates, setDates] = useState<Set<string> | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!location || !fittingType) {
      setDates(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(today.getTime() + BOOKING_HORIZON_DAYS * 24 * 60 * 60 * 1000);

    setLoading(true);
    getAvailableDatesInRange({
      location,
      startDate: isoDate(today),
      endDate: isoDate(end),
      durationMin: fittingType.durationMinutes,
      fittingType: resolveWireName(fittingType.name, fittingNameMap),
      resourceId: resourceId ?? undefined,
    })
      .then((set) => {
        if (cancelled) return;
        setDates(set);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setDates(new Set()); // empty set = nothing available
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [location, fittingType, resourceId, fittingNameMap]);

  return { availableDates: dates, loading };
}
